package fr.manyverse.app.presentation.layout

import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.LocalWindowInfo
import androidx.compose.ui.unit.dp

/**
 * Width-based size class following the Material 3 window size class breakpoints.
 *
 * This reflects the *actual* available width, so a tall-but-wide desktop window is
 * correctly treated as a large surface instead of a cramped phone-portrait layout.
 *
 * - [COMPACT]: < 600.dp — phones in portrait.
 * - [MEDIUM]: 600.dp–840.dp — tablets in portrait, large phones in landscape.
 * - [EXPANDED]: > 840.dp — tablets in landscape, desktop, web.
 */
enum class WindowWidth {
    COMPACT,
    MEDIUM,
    EXPANDED
}

/**
 * Resolves the current [WindowWidth] from the container width in dp.
 */
@Composable
fun rememberWindowWidth(): WindowWidth {
    val widthPx = LocalWindowInfo.current.containerSize.width
    val widthDp = with(LocalDensity.current) { widthPx.toDp() }
    return when {
        widthDp < 600.dp -> WindowWidth.COMPACT
        widthDp < 840.dp -> WindowWidth.MEDIUM
        else -> WindowWidth.EXPANDED
    }
}

/**
 * Layout container exposing the width-based [WindowWidth] size class.
 *
 * Use this when the decision is about *how much room* is available (single vs.
 * multi-column, list-detail), not merely orientation.
 */
@Composable
fun WindowSizeContainer(
    modifier: Modifier = Modifier,
    content: @Composable (WindowWidth) -> Unit
) {
    val windowWidth = rememberWindowWidth()
    BoxWithConstraints(modifier = modifier) {
        content(windowWidth)
    }
}
